import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSyncLogTypes, selectSyncLogClients } from "../../redux/syncLogSlice";
import { fetchImportConnectionsThunk, } from "../../redux/affiliateClickImportSlice";
// Networks this manual-import module supports (matches the backend list).
export const AFFILIATE_IMPORT_NETWORKS = [
    { value: "cj", label: "CJ" },
    { value: "impact", label: "Impact" },
    { value: "avantlink", label: "Avantlink" },
    { value: "commission_factory", label: "Commission Factory" },
    { value: "partnerize", label: "Partnerize" },
];
const NETWORK_LABEL = AFFILIATE_IMPORT_NETWORKS.reduce((acc, n) => ({ ...acc, [n.value]: n.label }), {});
// Formats a byte count as a compact size string (e.g. "1.4 MB").
export const formatFileSize = (bytes) => {
    if (!bytes)
        return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};
export const buildAffiliateImportClientOptions = (clients = []) => [...clients]
    .filter((client) => client?._id && client?.name)
    .map((client) => ({ value: String(client._id), label: String(client.name) }));
/**
 * Data source for the Import Clicks popup.
 *
 * - Clients come from the sync-log filter options (the same reliable, module-scoped
 *   list the Sync Log drawer uses).
 * - The client's connections (network + account name) come from a single, simple
 *   module endpoint — no decryption — which drives both the Network and Account
 *   Name dropdowns.
 */
export const useAffiliateImportOptions = (clientId, open, network) => {
    const dispatch = useDispatch();
    const syncLogClients = useSelector(selectSyncLogClients);
    const [connections, setConnections] = useState([]);
    const [connectionsLoading, setConnectionsLoading] = useState(false);
    // Fetch the client list once each time the popup opens.
    const hasFetchedClientsRef = useRef(false);
    useEffect(() => {
        if (open && !hasFetchedClientsRef.current) {
            hasFetchedClientsRef.current = true;
            dispatch(fetchSyncLogTypes({ status: "active" }));
        }
        if (!open)
            hasFetchedClientsRef.current = false;
    }, [open, dispatch]);
    // Fetch the selected client's connections (networks + account names).
    const loadConnections = useCallback(async (id) => {
        try {
            setConnectionsLoading(true);
            const data = await dispatch(fetchImportConnectionsThunk(id)).unwrap();
            setConnections(data || []);
        }
        catch {
            setConnections([]);
        }
        finally {
            setConnectionsLoading(false);
        }
    }, [dispatch]);
    useEffect(() => {
        if (open && clientId) {
            loadConnections(clientId);
        }
        else {
            setConnections([]);
        }
    }, [open, clientId, loadConnections]);
    const clientOptions = useMemo(() => buildAffiliateImportClientOptions(syncLogClients), [syncLogClients]);
    // The supported networks are a fixed list — the user picks one regardless of a
    // client, because "with advertiser" sheets need only a network.
    const networkOptions = useMemo(() => AFFILIATE_IMPORT_NETWORKS, []);
    // Account options only apply once a client is chosen (for sheets without an
    // advertiser column) — the selected client's connections for the chosen network.
    const connectionOptions = useMemo(() => {
        if (!clientId || !network)
            return [];
        return connections
            .filter((c) => c.network === network)
            .map((c) => ({ value: c.connection_id, label: c.account_name || NETWORK_LABEL[c.network] || "Account" }));
    }, [clientId, network, connections]);
    return {
        clientOptions,
        clientsLoading: false,
        networkOptions,
        networksLoading: false,
        connectionOptions,
        connectionsLoading: Boolean(open && clientId && network && connectionsLoading),
    };
};
